import React, { useState, useEffect } from "react";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import starFilledTokens from "@amzn/meridian-tokens/base/icon/star-filled";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Expander, Icon } from "@amzn/meridian";
import Loader from "@amzn/meridian/loader";
import {
  WasteProfilePathway,
  ClassifyWasteProfileResponse,
  SdsAttribute,
} from "src/types/classificationTypes";
import { classifyWasteProfile, initiateAiExternalResearch } from "src/services/classificationService";
import { useAppSelector } from "src/store/store";
import "./classification.scss";

function RemainingProfilesExpander({ remainingProfileIds }: { remainingProfileIds: string[] }) {
  const [expanded, setExpanded] = useState(false);
  if (remainingProfileIds.length === 0) return null;
  return (
    <>
      <Divider />
      <Expander title="View more profiles" type="inline" open={expanded} onChange={setExpanded}>
        <Column spacing="300" spacingInset="none">
          {remainingProfileIds.map((id, idx) => (
            <React.Fragment key={idx}>
              <Text type="h100">{id}</Text>
              {idx < remainingProfileIds.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Column>
      </Expander>
    </>
  );
}

function ReasoningExpander({ reasoning }: { reasoning: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <Divider />
      <Expander title="Why these profiles?" type="inline" open={expanded} onChange={setExpanded}>
        <Text type="b200" color="secondary">
          {reasoning}
        </Text>
      </Expander>
    </>
  );
}

const SDS_LIST_COLLAPSE_THRESHOLD = 6;

function SdsAttributeValue({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);
  const items = value.split(", ");
  if (items.length <= SDS_LIST_COLLAPSE_THRESHOLD) {
    return <Text type="b200">{value}</Text>;
  }
  const hiddenCount = items.length - SDS_LIST_COLLAPSE_THRESHOLD;
  const display = expanded
    ? value
    : `${items.slice(0, SDS_LIST_COLLAPSE_THRESHOLD).join(", ")}, +${hiddenCount} more`;
  return (
    <Column spacing="100" spacingInset="none" alignmentHorizontal="start">
      <Text type="b200">{display}</Text>
      <Button type="link" size="small" onClick={() => setExpanded((e) => !e)}>
        {expanded ? "Show less" : "Show all"}
      </Button>
    </Column>
  );
}

function SdsAttributesExpander({ sdsAttributes }: { sdsAttributes: SdsAttribute[] }) {
  const [expanded, setExpanded] = useState(false);
  if (sdsAttributes.length === 0) return null;
  return (
    <>
      <Divider />
      <Expander
        title="View SDS attributes"
        type="inline"
        spacingInset="none"
        open={expanded}
        onChange={setExpanded}
      >
        <Column width="100%" spacing="200" spacingInset="none">
          {sdsAttributes.map((attribute, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider />}
              <Row width="100%" widths={["35%", "fill"]} alignmentVertical="top" spacing="300">
                <Text type="b200" color="secondary">
                  {attribute.name}
                </Text>
                <SdsAttributeValue value={attribute.value} />
              </Row>
            </React.Fragment>
          ))}
        </Column>
      </Expander>
    </>
  );
}

// ---- Main component ----

interface WasteProfileCardProps {
  asin: string;
  itemName?: string;
  onLoadingChange?: (value: boolean) => void;
}

function WasteProfileCard({ asin, itemName, onLoadingChange }: WasteProfileCardProps) {
  const { activeSiteCode } = useAppSelector((state) => state.user);
  const [wasteProfileData, setWasteProfileData] = useState<ClassifyWasteProfileResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [aiResearchLoading, setAiResearchLoading] = useState(false);
  const [aiResearchError, setAiResearchError] = useState(false);

  const runAiExternalResearch = async () => {
    setAiResearchLoading(true);
    setAiResearchError(false);
    try {
      const res = await initiateAiExternalResearch({
        itemName: itemName ?? "",
        asin,
        siteName: activeSiteCode,
      });
      setWasteProfileData(res);
    } catch (error) {
      console.error("[WasteProfileCard] AI external research failed:", error);
      setAiResearchError(true);
    } finally {
      setAiResearchLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchClassification = async () => {
      setIsLoading(true);
      setWasteProfileData(null);
      try {
        const res = await classifyWasteProfile({
          itemName: itemName ?? "",
          asin,
          siteName: activeSiteCode,
        });
        if (!cancelled) {
          setWasteProfileData(res);
          onLoadingChange?.(false);
        }
      } catch (error) {
        console.error("[WasteProfileCard] Failed to classify waste profile:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    onLoadingChange?.(true);
    fetchClassification();
    return () => {
      cancelled = true;
    };
  }, [asin, itemName, activeSiteCode, retryCount]);

  if (isLoading) {
    return (
      <Column className="home-card" spacingInset="400" spacing="300">
        <Row spacing="300" alignmentVertical="center">
          <Loader size="small" type="circular" />
          <Text type="b300" color="secondary">
            Loading waste profile...
          </Text>
        </Row>
      </Column>
    );
  }

  if (!wasteProfileData) {
    return (
      <Column className="home-card" spacingInset="400" spacing="300">
        <Text type="b300" color="error">
          Unable to load waste profile. Please try again.
        </Text>
        <Button onClick={() => setRetryCount((c) => c + 1)} size="small" type="secondary">
          Retry
        </Button>
      </Column>
    );
  }

  const { recommendedWasteProfilesIds, allWasteProfilesIds, pathway, reasoning, sdsAttributes } =
    wasteProfileData;

  const recommendedSet = new Set(recommendedWasteProfilesIds);
  const remainingProfiles = allWasteProfilesIds.filter((id) => !recommendedSet.has(id));

  // Golden ASIN — all profiles shown
  if (pathway === WasteProfilePathway.GOLDEN_ASIN) {
    return (
      <Column className="home-card asin-waste-profile--golden" spacingInset="400 none none">
        <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
          <Icon tokens={starFilledTokens} />
          <Text type="h200" color="primary">
            Golden ASIN Profile
          </Text>
        </Row>
        <Column className="home-card" spacingInset="400" spacing="200">
          {allWasteProfilesIds.map((profileId, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider />}
              <Text type="h200">{profileId}</Text>
            </React.Fragment>
          ))}
          {reasoning && <ReasoningExpander reasoning={reasoning} />}
        </Column>
      </Column>
    );
  }

  // SDS attribute-based recommendation
  if (pathway === WasteProfilePathway.SDS_EXTRACTION) {
    return (
      <Column className="home-card asin-waste-profile--sds" spacingInset="400 none none">
        <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
          <Icon color="inverted" tokens={documentTokens} />
          <Text type="h200" color="inverted">
            SDS Attribute Based Recommendation
          </Text>
        </Row>
        <Column className="home-card" spacingInset="none">
          <Column spacingInset="400 500" spacing="300">
            {recommendedWasteProfilesIds.map((profileId, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <Divider />}
                <Text type="h200">{profileId}</Text>
              </React.Fragment>
            ))}
            {reasoning && (
              <Text type="b200" color="secondary">
                {reasoning}
              </Text>
            )}
            <RemainingProfilesExpander remainingProfileIds={remainingProfiles} />
            {sdsAttributes && <SdsAttributesExpander sdsAttributes={sdsAttributes} />}
          </Column>
        </Column>
      </Column>
    );
  }

  // AI-recommended: SIMILARITY offers a manual external-research upgrade;
  // AI_EXTERNAL_RESEARCH is the researched result.
  const isSimilarity = pathway === WasteProfilePathway.SIMILARITY;
  return (
    <Column className="home-card asin-waste-profile" spacingInset="400 none none">
      <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
        <Icon color="inverted" tokens={sparklesSmallTokens} />
        <Text type="h200" color="inverted">
          Profile Recommended by AI
        </Text>
      </Row>
      <Column className="home-card" spacingInset="none">
        <Column spacingInset="400 500" spacing="300">
          {recommendedWasteProfilesIds.map((profileId, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider />}
              <Text type="h200">{profileId}</Text>
            </React.Fragment>
          ))}
          <RemainingProfilesExpander remainingProfileIds={remainingProfiles} />
          {reasoning && <ReasoningExpander reasoning={reasoning} />}
          {isSimilarity && (
            <>
              <Text type="b200" color="secondary">
                This profile is similar to Golden ASIN based on the ingredients.
              </Text>
              {aiResearchLoading ? (
                <Row spacing="300" alignmentVertical="center">
                  <Loader size="small" type="circular" />
                  <Text type="b300" color="secondary">
                    Running AI external research...
                  </Text>
                </Row>
              ) : (
                <Column spacing="200" spacingInset="none" alignmentHorizontal="start">
                  <Button type="secondary" size="small" onClick={runAiExternalResearch}>
                    INITIATE AI EXTERNAL SEARCH
                  </Button>
                  {aiResearchError && (
                    <Text type="b200" color="error">
                      AI external research failed. Please try again.
                    </Text>
                  )}
                </Column>
              )}
            </>
          )}
        </Column>
      </Column>
    </Column>
  );
}

export default WasteProfileCard;
